import React, { useCallback, useState } from "react";
import { Alert, Button, useToggleState } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import useRequest from "../../../../reactHooks/useRequest";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import OAuthGrantCard from "./OAuthGrantCard";
import RevokeGrantModal from "./RevokeGrantModal";
import * as styles from "./styles.module.scss";

const messages = defineMessages( {
	loading: {
		id: "connectedApps.loading",
		defaultMessage: "Loading connected applications...",
	},
	loadError: {
		id: "connectedApps.loadError",
		defaultMessage: "Failed to load connected applications.",
	},
	retry: {
		id: "connectedApps.retry",
		defaultMessage: "Try again",
	},
	revokeFailed: {
		id: "connectedApps.revokeFailed",
		defaultMessage: "Failed to revoke access. Please try again.",
	},
	emptyState: {
		id: "connectedApps.emptyState",
		defaultMessage: "No applications are currently connected to your account.",
	},
} );

/**
 * Component for displaying and managing OAuth/OIDC connected applications.
 *
 * Allows users to view which applications have access to their
 * MyYoast account and revoke that access if desired.
 *
 * @param {Object} props Component props.
 * @param {Object} props.intl The react-intl object.
 *
 * @returns {JSX.Element} The connected applications component.
 */
export const ConnectedApplications = injectIntl( ( { intl } ) => {
	// Fetch grants using useRequest
	const { data, isLoading, isFirstLoad, error, reload } = useRequest( "oauth/grants" );
	const grants = data?.grants || [];

	// Modal state
	const [ revokeModalOpen, , , openRevokeModal, closeRevokeModal ] = useToggleState( false );
	const [ selectedGrant, setSelectedGrant ] = useState( null );

	// Revoke state
	const [ isRevoking, setIsRevoking ] = useState( false );
	const [ revokeError, setRevokeError ] = useState( null );

	/**
	 * Handles clicking the revoke button on a grant card.
	 *
	 * @param {Object} grant The grant to revoke.
	 *
	 * @returns {void}
	 */
	const handleRevokeClick = useCallback( ( grant ) => {
		setSelectedGrant( grant );
		setRevokeError( null );
		openRevokeModal();
	}, [ openRevokeModal ] );

	/**
	 * Handles confirming the revoke action.
	 *
	 * @returns {Promise<void>}
	 */
	const handleRevokeConfirm = useCallback( async() => {
		if ( ! selectedGrant ) {
			return;
		}

		setIsRevoking( true );
		setRevokeError( null );

		try {
			const response = await doRequest(
				prepareInternalRequest( `oauth/grants/${ selectedGrant.grantId }`, "DELETE" ),
			);

			if ( response.success ) {
				closeRevokeModal();
				setSelectedGrant( null );
				// Refresh the list
				reload();
			} else {
				setRevokeError( intl.formatMessage( messages.revokeFailed ) );
			}
		} catch ( err ) {
			setRevokeError( intl.formatMessage( messages.revokeFailed ) );
		} finally {
			setIsRevoking( false );
		}
	}, [ selectedGrant, closeRevokeModal, reload, intl ] );

	/**
	 * Handles closing the revoke modal.
	 *
	 * @returns {void}
	 */
	const handleModalClose = useCallback( () => {
		if ( ! isRevoking ) {
			closeRevokeModal();
			setSelectedGrant( null );
			setRevokeError( null );
		}
	}, [ isRevoking, closeRevokeModal ] );

	// Loading state
	if ( isFirstLoad && isLoading ) {
		return (
			<div className={ styles.loading }>
				<FormattedMessage { ...messages.loading } />
			</div>
		);
	}

	// Error state
	if ( error ) {
		return (
			<Alert type="error">
				<span className={ styles.errorContent }>
					<FormattedMessage { ...messages.loadError } />
					<Button variant="secondary" size="small" onClick={ reload } className={ styles.retryButton }>
						<FormattedMessage { ...messages.retry } />
					</Button>
				</span>
			</Alert>
		);
	}

	// Empty state
	if ( ! grants || grants.length === 0 ) {
		return (
			<p className={ styles.emptyState }>
				<FormattedMessage { ...messages.emptyState } />
			</p>
		);
	}

	// Auto-expand when there's only one app for better UX
	const shouldAutoExpand = grants.length === 1;

	return (
		<>
			<div className={ styles.grantsList }>
				{ grants.map( ( grant ) => (
					<OAuthGrantCard
						key={ grant.grantId }
						grant={ grant }
						onRevokeClick={ handleRevokeClick }
						initialExpanded={ shouldAutoExpand }
					/>
				) ) }
			</div>

			<RevokeGrantModal
				isOpen={ revokeModalOpen }
				grant={ selectedGrant }
				onClose={ handleModalClose }
				onConfirm={ handleRevokeConfirm }
				isRevoking={ isRevoking }
				error={ revokeError }
			/>
		</>
	);
} );

export default ConnectedApplications;
